import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import { authAPI } from '../services/api';
import { useThemeColors } from '../theme/ThemeContext';
import SEO from '../components/SEO';

const VerifyEmail = () => {
  const colors = useThemeColors();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const hasVerified = useRef(false);
  const lastVerificationTime = useRef(null); // Track last verification attempt

  const token = searchParams.get('token');

  const verifyEmail = async () => {
    console.log('🔍 Starting email verification with token:', token);
    
    // Prevent multiple verification attempts within 5 seconds
    const now = Date.now();
    if (isVerifying || hasVerified.current || (lastVerificationTime.current && (now - lastVerificationTime.current < 5000))) {
      console.log('🔄 Verification already in progress or too recent, skipping...');
      return;
    }
    
    if (!token) {
      setStatus('error');
      setMessage('Verification token is missing');
      return;
    }

    // Set states to prevent multiple calls
    setIsVerifying(true);
    lastVerificationTime.current = now;
    
    try {
      const response = await authAPI.verifyEmail(token);
      console.log('📥 Verification response:', response.data);
      
      if (response.data.success) {
        // Mark as completed to prevent future attempts
        hasVerified.current = true;
        
        if (response.data.data.alreadyVerified) {
          console.log('✅ Email already verified');
          setStatus('success');
          setMessage('Your email is already verified! You can now login to your account.');
          toast.success('Email already verified!');
        } else {
          console.log('✅ Email verified successfully');
          setStatus('success');
          setMessage('Your email has been successfully verified! You can now login to your account.');
          toast.success('Email verified successfully!');
        }
      } else {
        console.log('❌ Verification failed:', response.data.message);
        setStatus('error');
        setMessage(response.data.message || 'Email verification failed');
      }
    } catch (error) {
      console.log('💥 Verification error:', error.response?.data?.message || error.message);
      setStatus('error');
      setMessage(error.response?.data?.message || 'Email verification failed. The link may have expired.');
    } finally {
      // Reset verifying state after completion
      setIsVerifying(false);
    }
  };

  // Auto-verify if token exists in URL
  React.useEffect(() => {
    if (token && !isVerifying && !hasVerified.current) {
      verifyEmail();
    }
  }, []); // Remove dependency to prevent re-runs

  const handleGoToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-theme-background flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <SEO 
        title="Verify Email" 
        description="Verify your TripTrot account email address to activate your account and start using the platform"
        keywords={['email verification', 'account activation', 'verify email', 'trip trot account']}
        canonical="/verify-email"
        noindex={true}
      />
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <Mail className="mx-auto h-12 w-12 text-theme-primary" />
          <h2 className="mt-6 text-3xl font-extrabold text-theme-primary">
            Email Verification
          </h2>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-theme-card py-8 px-4 shadow sm:rounded-lg sm:px-10 border border-theme-light">
          <div className="text-center">
            {status === 'loading' && (
              <div className="space-y-4">
                <Loader2 className="mx-auto h-12 w-12 text-theme-primary animate-spin" />
                <p className="text-theme-secondary">Verifying your email address...</p>
              </div>
            )}

            {status === 'success' && (
              <div className="space-y-4">
                <CheckCircle className="mx-auto h-12 w-12 text-theme-success" />
                <div>
                  <h3 className="text-lg font-medium text-theme-primary">
                    Email Verified Successfully!
                  </h3>
                  <p className="mt-2 text-sm text-theme-secondary">
                    {message}
                  </p>
                </div>
                <button
                  onClick={handleGoToLogin}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-theme-inverse bg-theme-primary hover:bg-theme-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-theme-primary"
                >
                  Go to Login
                </button>
              </div>
            )}

            {status === 'error' && (
              <div className="space-y-4">
                <XCircle className="mx-auto h-12 w-12 text-theme-error" />
                <div>
                  <h3 className="text-lg font-medium text-theme-primary">
                    Verification Failed
                  </h3>
                  <p className="mt-2 text-sm text-theme-secondary">
                    {message}
                  </p>
                  <button
                    onClick={handleGoToLogin}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-theme-inverse bg-theme-primary hover:bg-theme-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-theme-primary"
                  >
                    Go to Login
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
